import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Music, Heart, Upload, Video, Download, Globe, Lock, Trash2, Play, Settings2, Check, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { coverFor, fmtTime } from "@/components/common";

export default function Library() {
  const [tab, setTab] = useState("songs");
  const [songs, setSongs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [liked, setLiked] = useState([]);
  const [settingsFor, setSettingsFor] = useState(null);
  const { playSong } = usePlayer();
  const { openDownload, openLyricVideo } = useOutletContext();
  const [upTitle, setUpTitle] = useState("");
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/songs/mine").then((r) => setSongs(r.data.songs)).catch(() => {});
    api.get("/lyric-videos").then((r) => setVideos(r.data.videos)).catch(() => {});
    api.get("/me/likes").then((r) => setLiked(r.data.songs)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const withStream = (s) => ({ ...s, stream_url: `${api.defaults.baseURL}/stream/${s.id}`, creator_name: s.creator_name || "You" });

  const setVisibility = async (s, v) => { await api.put(`/songs/${s.id}`, { visibility: v }); toast.success(`Set to ${v}`); load(); };
  const toggleDownload = async (s, key) => {
    const cur = s.downloads || {};
    await api.put(`/songs/${s.id}`, { downloads: { ...cur, [key]: !cur[key] } });
    load();
  };
  const del = async (s) => { if (!window.confirm("Delete this song?")) return; await api.delete(`/songs/${s.id}`); load(); };
  const rename = async (s) => { const t = window.prompt("New title", s.title); if (t) { await api.put(`/songs/${s.id}`, { title: t }); load(); } };

  const upload = async () => {
    if (!upFile || !upTitle.trim()) return toast.error("Add a title and file");
    setUploading(true);
    const fd = new FormData(); fd.append("file", upFile); fd.append("title", upTitle); fd.append("genre", "other");
    try { await api.post("/songs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); setUpTitle(""); setUpFile(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setUploading(false);
  };

  const dlVideo = async (v) => {
    try { const res = await api.get(`/lyric-videos/${v.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${v.song_title} - JixxeoX.mp4`; a.click(); URL.revokeObjectURL(url); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6">
      <h1 className="text-2xl font-bold font-display mb-4">My Library</h1>
      <div className="flex gap-2 mb-5 border-b border-white/10 overflow-x-auto no-scrollbar">
        {[["songs", "My Songs"], ["videos", "Lyric Videos"], ["liked", "Liked"], ["upload", "Upload"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`lib-tab-${k}`} className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${tab === k ? "border-purple-500 text-white" : "border-transparent text-slate-400"}`}>{l}</button>
        ))}
      </div>

      {tab === "songs" && (
        <div className="space-y-2">
          {songs.length === 0 && <p className="text-slate-500 text-sm py-6">No songs yet. Create one in the Studio.</p>}
          {songs.map((s) => (
            <div key={s.id} className="jx-card rounded-xl p-3" data-testid={`lib-song-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0 cursor-pointer" onClick={() => playSong(withStream(s), songs.map(withStream))}>
                  <img src={coverFor(s)} className="w-full h-full object-cover" alt="" />
                  <div className="absolute inset-0 bg-black/40 grid place-items-center"><Play size={16} /></div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{s.title}</p>
                  <p className="text-xs text-slate-500">{s.source === "ai" ? "AI" : "Upload"} • {s.visibility} • {s.plays} plays • {fmtTime(s.duration)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setSettingsFor(settingsFor === s.id ? null : s.id)} title="Download settings" className="p-2 text-slate-400 hover:text-white"><Settings2 size={16} /></button>
                  <button onClick={() => openLyricVideo(withStream(s))} className="p-2 text-slate-400 hover:text-cyan-400"><Video size={16} /></button>
                  <button onClick={() => openDownload(withStream(s))} className="p-2 text-slate-400 hover:text-purple-400"><Download size={16} /></button>
                  <button onClick={() => del(s)} className="p-2 text-slate-500 hover:text-rose-400"><Trash2 size={16} /></button>
                </div>
              </div>
              {settingsFor === s.id && (
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-3 text-xs">
                  <button onClick={() => setVisibility(s, s.visibility === "public" ? "private" : "public")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5">
                    {s.visibility === "public" ? <Globe size={13} className="text-green-400" /> : <Lock size={13} />} {s.visibility === "public" ? "Published" : "Private"}
                  </button>
                  <button onClick={() => setVisibility(s, "unlisted")} className="px-3 py-1.5 rounded-lg bg-white/5">Unlisted</button>
                  <button onClick={() => rename(s)} className="px-3 py-1.5 rounded-lg bg-white/5">Rename</button>
                  <span className="text-slate-500">Downloads:</span>
                  {["mp3", "wav", "lyric_video", "stream_only"].map((k) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={!!(s.downloads || {})[k]} onChange={() => toggleDownload(s, k)} data-testid={`dl-toggle-${k}-${s.id}`} />
                      <span className="capitalize">{k.replace("_", " ")}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "videos" && (
        <div className="grid sm:grid-cols-2 gap-3">
          {videos.length === 0 && <p className="text-slate-500 text-sm py-6">No lyric videos yet.</p>}
          {videos.map((v) => (
            <div key={v.id} className="jx-card rounded-xl p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg jx-cta grid place-items-center"><Video size={20} className="text-white" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{v.song_title}</p>
                <p className="text-xs text-slate-500">{v.template} • {v.aspect} • {v.quality} • {v.status}</p>
              </div>
              {v.status === "ready" ? <button onClick={() => dlVideo(v)} className="jx-cta text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1"><Download size={13} /> MP4</button>
                : v.status === "processing" ? <Loader2 className="animate-spin text-cyan-400" size={16} />
                : <span className="text-xs text-rose-400">Failed</span>}
            </div>
          ))}
        </div>
      )}

      {tab === "liked" && (
        <div className="space-y-2">
          {liked.length === 0 && <p className="text-slate-500 text-sm py-6">No liked songs yet.</p>}
          {liked.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 cursor-pointer" onClick={() => playSong(s, liked)}>
              <img src={coverFor(s)} className="w-11 h-11 rounded-lg object-cover" alt="" />
              <div className="min-w-0 flex-1"><p className="text-sm truncate">{s.title}</p><p className="text-xs text-slate-500 truncate">{s.creator_name}</p></div>
              <Heart size={15} className="text-rose-400 fill-rose-400" />
            </div>
          ))}
        </div>
      )}

      {tab === "upload" && (
        <div className="jx-card rounded-2xl p-5 max-w-md">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Upload size={17} className="text-cyan-400" /> Upload your music</h2>
          <input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="Song title" className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm mb-3" />
          <input type="file" accept="audio/*" onChange={(e) => setUpFile(e.target.files[0])} data-testid="upload-file" className="w-full text-sm text-slate-400 mb-3 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-500/20 file:text-purple-300" />
          <button onClick={upload} disabled={uploading} data-testid="upload-btn" className="jx-cta text-white text-sm font-semibold px-5 py-2.5 rounded-xl disabled:opacity-50">{uploading ? "Uploading…" : "Upload"}</button>
        </div>
      )}
    </div>
  );
}
